<template v-cloak>
  <div>
    <YearlySwitchAlert ref="alertRef" />

    <div class="relative" style="height: 3.75rem">
      <div class="half-bg bg-white"></div>
      <div class="half-bg bg-repeat-x bg-seperator-start gray-bg"></div>
    </div>
    <div class="gray-bg">
      <section class="plans-section">
        <PaygProductSelect :products="products" />

        <SubscriptionPlan
          :plans="monthlyPlans"
          :current-plan="currentPlan"
          billing-interval="monthly"
          :selected-volume="selectedVolume"
          @buy="buyPlan"
          @alert="openAlert"
          @volume-changed="updateSelectedVolume"
        >
          <template #title> {{ I18n.t("pricing.pay_monthly") }}</template>
        </SubscriptionPlan>

        <SubscriptionPlan
          :plans="yearlyPlans"
          :current-plan="currentPlan"
          billing-interval="yearly"
          :selected-volume="selectedVolume"
          @buy="buyPlan"
          @alert="openAlert"
          @volume-changed="updateSelectedVolume"
        >
          <template #title>{{ I18n.t("pricing.pay_yearly") }}</template>
        </SubscriptionPlan>
      </section>
      <div class="flex justify-center">
        <p
          @click="volumeCardCtaClicked()"
          class="text-typo-secondary text-sm !mb-0 px-4 text-center"
          v-html="I18n.t('pricing.need_more_than_100_000_images_per_year', { link: '/enterprise/contact' })"
        ></p>
      </div>
    </div>
  </div>
  <IncludedFeaturesSection />
  <div class="gray-bg-separator gray-bg w-full bg-seperator-end bg-repeat-x"></div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, onMounted, ref } from "vue";
import SubscriptionPlan from "./subscription_plan.vue";
import { BillingInterval, Plan, PlansData, ProductsData } from "./types";
import YearlySwitchAlert from "./yearly_switch_alert.vue";
import PaygProductSelect from "./payg_product_select.vue";
import { rbgPricingCardCtaClickedV100, rbgSalesContactCtaClickedV100 } from "kaleido-event-tracker";
import { fromSubscriptionPlan } from "./events";
import IncludedFeaturesSection from "./included_features_section.vue";

const props = defineProps<{
  plansData: string;
  productsData: string;
}>();

const alertRef = ref(null);
const openAlert = () => {
  alertRef.value?.show();
};

const parsedPlansData: ComputedRef<PlansData> = computed(() => JSON.parse(props.plansData));
const productsData = computed(() => JSON.parse(props.productsData) as ProductsData);
const products = computed(() => productsData.value.products);

onMounted(() => {
  // Autobuy plan
  const shouldAutobuyPlan = computed(() => parsedPlansData.value.shouldAutobuyPlan);
  const selectedPlanForAutobuy = computed(() => parsedPlansData.value.selectedPlanForAutobuy);
  if (shouldAutobuyPlan.value) {
    window.doPurchase(selectedPlanForAutobuy.value.pPlanId, "plan", selectedPlanForAutobuy.value.paddleCoupon);
  }

  // Autobuy product
  const shouldAutobuyProduct = computed(() => productsData.value.shouldAutobuyProduct);
  const selectedProductForAutobuy = computed(() => productsData.value.selectedProductForAutobuy);
  if (shouldAutobuyProduct.value) {
    window.doPurchase(selectedProductForAutobuy.value?.pProductId, "product");
  }
});
const I18n = inject("I18n");

const selectedVolume = ref<number | null>(null);

const updateSelectedVolume = (volume: number) => {
  selectedVolume.value = volume;
};

const monthlyPlans = computed((): Plan[] => {
  const extractedPlans = Object.entries(parsedPlansData.value.plansByCredits);
  return extractedPlans
    .map(([_, plans]) => plans.find((plan) => plan.billingInterval === "monthly") || plans[0])
    .filter(Boolean);
});

const yearlyPlans = computed((): Plan[] => {
  const extractedPlans = Object.entries(parsedPlansData.value.plansByCredits);
  return extractedPlans
    .map(([_, plans]) => plans.find((plan) => plan.billingInterval === "yearly") || plans[0])
    .filter(Boolean);
});

const currentPlan = computed(() => {
  return parsedPlansData.value.currentPlan;
});

const buyPlan = (plan: Plan) => {
  const { pPlanId, paddleCoupon } = plan;

  rbgPricingCardCtaClickedV100({
    type: fromSubscriptionPlan(plan),
    credit_volume: plan.monthlyCredits,
    billing_interval: plan.billingInterval,
  });

  window.doPurchase(pPlanId, "plan", paddleCoupon);
};

const volumeCardCtaClicked = () => {
  rbgSalesContactCtaClickedV100({
    touchpoint_label: "pricing-volume-card",
  });
};
</script>

<style scoped lang="scss">
.plans-section {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  max-width: 60rem;
  margin: 0 auto;

  @media (max-width: 767px) {
    grid-template-columns: repeat(1, 1fr);
    padding: 1rem 1rem 1.5rem 1rem;
  }

  @media (min-width: 550px) {
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }
}

.half-bg {
  height: 50%;
}

.gray-bg {
  background-color: #f1f3f4;
}

.gray-bg-separator {
  height: 1.5rem;
}

.billing-switch-container {
  position: absolute;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}

.link-in-description {
  & a {
    text-decoration: none !important;
    font-weight: 500;
  }
}
</style>
